import cn from 'clsx'

// Components
import { Toast } from '@tracklib/kit'

// Types
import { UserSongRightsholder } from 'types'
import Percent from 'components/kit/percent/percent'

import styles from './rightsholderstable.css'

type RightsholdersTableProps = {
  percentageModifier?: string
  rightsholders: Array<UserSongRightsholder>
}

const RightsholdersTable = ({ percentageModifier, rightsholders: rightsholdersUnsorted }: RightsholdersTableProps): JSX.Element | null => {
  let rightsholdersData

  const rightsholders = rightsholdersUnsorted.sort((a, b) => a.id - b.id)
  const publishers = rightsholders.filter(r => r.type === 'PUBLISHER')
  const writers = rightsholders.filter(r => r.type === 'WRITER')
  switch (percentageModifier) {
    case 'bmi': {
      rightsholdersData = rightsholders.map(r => ({
        ...r,
        share: r.share * 2,
      }))

      break
    }
    case 'stim': {
      const originalPublishers = publishers.filter(({ derivative_rightsholder }) => !derivative_rightsholder)
      const derivativePublishers = publishers.filter(({ derivative_rightsholder }) => derivative_rightsholder)
      const derivativeWriters = writers.filter(({ derivative_rightsholder }) => derivative_rightsholder)
      const nonConnectedDerivativePublishersTotalShare = derivativePublishers
        .filter(
          ({ ipi_number }) => !derivativeWriters.some(({ connected_to_ipi }) => connected_to_ipi && +connected_to_ipi === +ipi_number),
        )
        .reduce<number>((agg, { share }) => agg + share, 0)
      const derivativeWritersWithDerivativePublishersShare = derivativeWriters.map(writer => {
        const { connected_to_ipi, share } = writer
        const connectedPublisher = derivativePublishers.find(({ ipi_number }) => connected_to_ipi && +ipi_number === +connected_to_ipi)
        return {
          ...writer,
          share: share + (connectedPublisher?.share || 0) + nonConnectedDerivativePublishersTotalShare / derivativeWriters.length,
        }
      })
      const originalWriters = writers.filter(({ derivative_rightsholder }) => !derivative_rightsholder)
      const originalPublishersTotalShare = originalPublishers.reduce<number>((agg, { share }) => agg + share, 0)
      const originalWritersTotalShare = originalWriters.reduce<number>((agg, { share }) => agg + share, 0)
      const originalWritersWithPublishersShare = originalWriters.map(({ share, ...rest }) => {
        const ratio = share / originalWritersTotalShare
        return {
          ...rest,
          share: share + originalPublishersTotalShare * ratio,
        }
      })
      rightsholdersData = [...originalWritersWithPublishersShare, ...derivativeWritersWithDerivativePublishersShare]
      break
    }

    default: {
      rightsholdersData = rightsholders
    }
  }
  const sortedRightsholdersData = rightsholdersData.sort((a, b) => a.id - b.id)
  const hasModified = sortedRightsholdersData.some(rightsholder => rightsholder.modified)

  return (
    <>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>IPI</th>
            <th>Share</th>
          </tr>
        </thead>
        <tbody>
          {sortedRightsholdersData.map(({ name, ipi_number, share, type, modified }, index) => (
            <tr key={`${String(index)}-${ipi_number}`} className={cn(modified && type === 'WRITER' && styles.modified)}>
              <td>{name}</td>
              <td>{type === 'WRITER' ? 'Writer' : 'Publisher'}</td>
              <td>{ipi_number}</td>
              <td>
                <Percent number={+share.toFixed(2)} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {hasModified && (
        <Toast
          description="While the numbers marked red are not correct, they are a suggestion how to register using your CMO. Register using these numbers and we will correct this registration for you using our partner, Songtrust."
          type="neutral"
        />
      )}
    </>
  )
}

export default RightsholdersTable
